import { toast } from 'react-toastify'
import axios from './api'
import { getApiErrorMessage } from './apiError'

interface DeleteResourceTypes {
  id: number
  closeDialog: () => void
  mutate: (value: string) => void
  api: string
  toastError: string
  toastSuccess: string
  swrKey: string
  token: string | null
}

/**
 * The detail URL for a resource, trailing slash included.
 *
 * Every API route is registered through a DRF router, so every detail route
 * ends in a slash. A slashless GET is rescued by `APPEND_SLASH` - it 301s to
 * the slashed URL - but a DELETE is not, and Django says so itself:
 *
 *   RuntimeError: You called this URL via DELETE, but the URL doesn't end in
 *   a slash and you have APPEND_SLASH set. Django can't redirect to the slash
 *   URL while maintaining DELETE data.
 *
 * So `/api/report/category/1` answered 500 while `/api/report/category/1/`
 * reaches the view. That 500 was every "failed to delete" the admin screens
 * showed - categories, branches and users alike.
 *
 * Callers write the `api` prefix inconsistently (with and without either
 * slash), so both ends are normalised here rather than at each call site.
 */
export const deleteResourceUrl = (api: string, id: number): string =>
  `/${api.replace(/^\/+/, '').replace(/\/+$/, '')}/${id}/`

export const deleteResource = async ({
  id,
  api,
  closeDialog,
  mutate,
  toastError,
  toastSuccess,
  swrKey,
  token,
}: DeleteResourceTypes) => {
  try {
    const response = await axios.delete(deleteResourceUrl(api, id), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (response.status === 204 || response.status === 200) {
      toast.success(toastSuccess)
      mutate(swrKey)
      closeDialog()
    }
  } catch (error) {
    // The backend refuses some deletes with a reason of its own - "Cannot
    // delete last category!" - which the generic message used to throw away.
    toast.error(getApiErrorMessage(error) ?? toastError)
  }
}
